import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BackButton } from '../../components/ui';
import { COLORS } from '../../lib/styleGuide';

// Global design system constants
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';

const BuyerPolicies = () => {
  const navigate = useNavigate();
  // Buyer-specific policies plus "both" policies as per PRD
  const buyerPolicies = [
    {
      id: 'buyer-policy',
      title: 'Buyer Policy',
      description: 'Specific responsibilities and expectations for buyers using the Vesta platform.',
      slug: 'buyer-policy',
      role: 'buyer'
    },
    {
      id: 'delivery-pickup-policy',
      title: 'Order, Delivery & Pickup Policy',
      description: 'Local delivery and pickup procedures, safe handling, and proof of handoff requirements.',
      slug: 'delivery-pickup',
      role: 'both'
    },
    {
      id: 'preorder-custom-policy',
      title: 'Preorder & Custom/Made-to-Order Policy',
      description: 'Lead times, deposits, cancellations, force majeure, and custom order management.',
      slug: 'preorder-custom',
      role: 'both'
    },
    {
      id: 'refunds-cancellations-disputes',
      title: 'Refunds, Cancellations & Disputes',
      description: 'Perishable goods rules, timelines, escalation procedures, and dispute resolution.',
      slug: 'refunds-cancellations-disputes',
      role: 'both'
    },
    {
      id: 'community-content-policy',
      title: 'Community & Content Policy',
      description: 'Video standards, reviews, respectful conduct, and community guidelines.',
      slug: 'community-content',
      role: 'both'
    },
    {
      id: 'content-community-guidelines',
      title: 'Content & Community Guidelines',
      description: 'Detailed guidelines for content creation, community interaction, and professional standards.',
      slug: 'content-community-guidelines',
      role: 'both'
    },
    {
      id: 'intellectual-property-dmca',
      title: 'Intellectual Property & DMCA Policy',
      description: 'Copyright protection, trademark rights, DMCA procedures, and IP violation reporting.',
      slug: 'intellectual-property-dmca',
      role: 'both'
    },
    {
      id: 'privacy-policy',
      title: 'Privacy Policy',
      description: 'Full policy covering data collection, use, sharing, rights, and international transfers.',
      slug: 'privacy',
      role: 'both'
    },
    {
      id: 'prohibited-items-activities',
      title: 'Prohibited Items & Activities Policy',
      description: 'Banned foods, labeling violations, unsafe content, and prohibited business practices.',
      slug: 'prohibited-items',
      role: 'both'
    },
    {
      id: 'terms-service-user-agreement',
      title: 'Terms of Service / User Agreement',
      description: 'Comprehensive user agreement covering all aspects of platform usage and user obligations.',
      slug: 'terms-service-user-agreement',
      role: 'both'
    },
    {
      id: 'terms-of-service',
      title: 'Terms of Service',
      description: 'Complete terms governing your use of the Vesta platform, including user rights and responsibilities.',
      slug: 'terms',
      role: 'both'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: NEUTRAL_BG }}>
      {/* Header */}
      <header style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '16px 20px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <h1 style={{ 
              fontFamily: 'Sora, sans-serif',
              fontWeight: 700,
              fontSize: 20, 
              color: NEUTRAL_TEXT, 
              margin: 0 
            }}>
              Legal Center
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" style={{ paddingTop: '72px' }}>
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
            Buyer Policies
          </h1>
          <p className="text-xl" style={{ color: ACCENT_GRAY, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
            All policies that apply to buyers, plus general platform policies.
          </p>
        </div>

        {/* View All Policies Link */}
        <div className="text-center mb-8">
          <Link
            to="/legal"
            className="inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-colors"
            style={{ 
              background: COLORS.PRIMARY_GRADIENT,
              backgroundSize: '200% 200%',
              animation: 'gradientShift 3s ease infinite',
              color: NEUTRAL_WHITE,
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700
            }}
            onMouseEnter={(e) => e.currentTarget.style.animation = 'gradientShift 1s ease infinite'}
            onMouseLeave={(e) => e.currentTarget.style.animation = 'gradientShift 3s ease infinite'}
            aria-label="View all policies"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            View All Policies
          </Link>
        </div>

        {/* Policy Cards */}
        <div className="grid gap-6">
          {buyerPolicies.map((policy) => (
            <div
              key={policy.id}
              className="rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-200"
              style={{ backgroundColor: NEUTRAL_WHITE }}
            >
              <Link
                to={`/legal/${policy.slug}`}
                className="block group"
                aria-label={`Read full ${policy.title} policy`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold transition-colors duration-200 group-hover:text-orange-600" 
                           style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                        {policy.title}
                      </h3>
                    </div>
                    <p style={{ color: ACCENT_GRAY, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
                      {policy.description}
                    </p>
                  </div>
                  <div className="ml-4 transition-colors duration-200 group-hover:text-orange-500" 
                       style={{ color: ACCENT_GRAY }} aria-hidden="true">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

      </main>
    </div>
  );
};

export default BuyerPolicies; 